import { useTranslation } from "react-i18next";

import MetricSelector from "common/components/MetricSelector";
import RecentLeads from "common/components/RecentLeads";
import RenderIf from "common/components/RenderIf";
import RestrictedComponent from "common/components/RestrictedComponent";
import StatisticsGraph from "common/components/StatisticsGraph";
import TimeframeSelector from "common/components/TimeframeSelector";
import useMetrics from "common/datahooks/useMetrics";
import useSelectedMetric from "common/hooks/useSelectedMetric";
import useSelectedTimeframe from "common/hooks/useSelectedTimeframe";
import type { Metrics } from "common/types";

import { CAMPAIGN_OUTREACH_OPTIONS } from "../../../../constants";
import { useCampaignContext } from "../../../../context/CampaignContext";
import useFlowMetrics from "../../../../datahooks/useFlowMetrics";
import { getFlowTitle } from "../../../../utils";
import AutoLabels from "../../AutoLabels";
import CampaignFlow from "../../CampaignFlow";
import FlowTypeLabel from "../../FlowTypeLabel";
import SectionHeading from "../../SectionHeading";
import ChangeFlowName from "./ChangeFlowName";
import PauseCampaign from "./PauseCampaign";

import type { CampaignFlow as FlowType } from "../../../../types";

const timeframeLocalStorageKey = "campaign-timeframe";

interface FlowDetailsProps {
  flow: FlowType;
  openLeadPreview: (leadId: string) => void;
}

export default function FlowDetails({
  flow,
  openLeadPreview,
}: FlowDetailsProps) {
  const { t } = useTranslation("campaigns", { keyPrefix: "flowTypes" });

  const campaign = useCampaignContext();
  const {
    outreach_type: outreachType,
    state,
    owners,
    id: campaignId,
    labels,
  } = campaign;

  const { type, id: flowId } = flow;
  const isDrip = outreachType === "drip";

  const isPrimaryFlow =
    type === "PRIMARY_CONNECT" || type === "PRIMARY_MESSAGE";

  const { flowMetrics, isLoadingFlowMetrics } = useFlowMetrics(
    campaignId,
    flowId,
  );

  const { selectedTimeframe, setSelectedTimeframe, selectedTimeframeOption } =
    useSelectedTimeframe(
      JSON.parse(localStorage.getItem(timeframeLocalStorageKey)),
    );

  const {
    metrics,
    metricsSum,
    refetchMetrics,
    metricsError,
    isLoadingMetrics,
  } = useMetrics({
    flowId: flowId,
    selectedTimeframe,
    accountIds: [],
  });

  const isLoadingMetricsData = isLoadingMetrics || isLoadingFlowMetrics;

  const hiddenMetricsColumns: (keyof Metrics)[] = [
    "views",
    ...CAMPAIGN_OUTREACH_OPTIONS[outreachType].hiddenMetricsColumns,
    ...(type === "INMAIL_OPTIMIZATION"
      ? ([
          "sent_connections",
          "sent_messages",
          "accepted_connections",
          "message_requests",
        ] as (keyof Metrics)[])
      : (["sent_inmails"] as (keyof Metrics)[])),
  ];

  const { setSelectedMetric, selectedMetric } = useSelectedMetric({
    hiddenMetrics: hiddenMetricsColumns,
  });

  const canEditFlow =
    state === "CREATED" || state === "PAUSED" || state === "SCHEDULED";

  return (
    <>
      <section className="flex flex-col gap-4 lg:flex-row items-start">
        <SectionHeading
          title={`${getFlowTitle(flow)} overview`}
          subtitle="A detailed overview of all the interactions in this flow"
        />

        <TimeframeSelector
          shouldOffsetTimezone
          className="ml-auto"
          selectedTimeframe={selectedTimeframe}
          selectTimeframe={(selection) => {
            setSelectedTimeframe(selection);
            localStorage.setItem(
              timeframeLocalStorageKey,
              JSON.stringify(selection),
            );
          }}
        />
      </section>

      <div className="flex flex-col gap-y-2.5">
        <MetricSelector
          metricsSum={metricsSum}
          selectedMetric={selectedMetric}
          setSelectedMetric={setSelectedMetric}
          totalMetrics={flowMetrics}
          isLoading={isLoadingMetricsData}
          hiddenColumns={hiddenMetricsColumns}
        />

        <StatisticsGraph
          selectedMetric={selectedMetric}
          metrics={metrics}
          metricsSum={metricsSum}
          requestError={metricsError}
          isLoading={isLoadingMetricsData}
          isHourly={
            selectedTimeframeOption.getParameters({ campaign }).bucketSize ===
            "1 hour"
          }
          refetchMetrics={refetchMetrics}
        />
      </div>

      <RestrictedComponent visibleFor={["campaigns_full"]}>
        <RenderIf condition={state === "ACTIVE"}>
          <PauseCampaign campaignId={campaignId} />
        </RenderIf>
      </RestrictedComponent>

      <RestrictedComponent enabledFor={["campaigns_full"]}>
        {(isDisabled) => (
          <div className="mt-4 rounded-3xl border border-black-200 p-3">
            {/* Section heading */}
            <div className="-mx-3 mb-3 flex gap-3 border-b border-black-200 px-3 pb-3">
              <div className="flex gap-3">
                <FlowTypeLabel type={type} />

                <div>
                  <h3 className="mb-1 text-lg-bold">{getFlowTitle(flow)}</h3>
                  <p className="text-black-500">{t(`${type}_Description`)}</p>
                </div>
              </div>

              <RenderIf condition={isPrimaryFlow}>
                <ChangeFlowName
                  isDisabled={isDisabled || !canEditFlow}
                  flow={flow}
                />
              </RenderIf>
            </div>

            <CampaignFlow
              flow={flow}
              canEditFlow={!isDisabled && canEditFlow}
            />
          </div>
        )}
      </RestrictedComponent>

      <RestrictedComponent visibleFor={["leads_full"]}>
        {/* Recent leads */}
        <section className="mt-8">
          <div className="flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center mb-4">
            {/* Section heading */}
            <div className="flex flex-col gap-0">
              <h3 className="mb-1 text-xl-bold">
                Recent {isDrip ? "replies" : "leads"}
              </h3>

              <p className="text-black-500">
                The latest {isDrip ? "responses" : "leads"} you have gained
                through this flow
              </p>
            </div>

            {/* Auto labels */}
            <AutoLabels campaignId={campaignId} labels={labels} />
          </div>

          {/* Recent leads */}
          <RecentLeads
            campaignId={campaignId}
            flowId={flow.id}
            accountIds={owners}
            isDrip={isDrip}
            openPreviewSidebar={openLeadPreview}
          />
        </section>
      </RestrictedComponent>
    </>
  );
}
